export type SortByOption =
    | 'sort_on_alphabet'
    | 'sort_on_value_sales'
    | 'sort_on_volume_sales'
    | 'sort_on_pack_size';
export type SortOrder = 'asc' | 'desc';

export type AxisConfiguration = {
    sortBy: SortByOption;
    sortOrder: SortOrder;
    showTop: number;
    groupRestAsOther: boolean;
    showOwnVendorFirst: boolean;
};

export type AxisPreviewItem = {
    name: string;
    valueSales: number;
    volumeSales: number;
    isOwn?: boolean;
    /** Set when the API consolidates remaining items (ex_id `__other__`). */
    isOtherGroup?: boolean;
    members?: string[];
};

export type PreviewListEntry =
    | { kind: 'item'; name: string; isOwn: boolean }
    | { kind: 'other'; label: string; hiddenItems: string[] };

export type AxisPreviewResult = {
    entries: PreviewListEntry[];
    visibleCount: number;
    totalCount: number;
    valueSalesPercent: number;
    volumeSalesPercent: number;
};

type AxisConfigurationDefaults = {
    sortBy?: SortByOption;
    sortOrder?: SortOrder;
    groupRestAsOther?: boolean;
    showOwnVendorFirst?: boolean;
};

export const createDefaultAxisConfiguration = (
    totalCount: number,
    defaults?: AxisConfigurationDefaults,
): AxisConfiguration => ({
    sortBy: defaults?.sortBy ?? 'sort_on_alphabet',
    sortOrder: defaults?.sortOrder ?? 'asc',
    showTop: totalCount,
    groupRestAsOther: defaults?.groupRestAsOther ?? false,
    showOwnVendorFirst: defaults?.showOwnVendorFirst ?? false,
});

const sortPreviewItems = (items: AxisPreviewItem[], config: AxisConfiguration): AxisPreviewItem[] => {
    const sorted = [...items];

    if (config.sortBy === 'sort_on_alphabet') {
        sorted.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
        );
        if (config.sortOrder === 'desc') {
            sorted.reverse();
        }
        return sorted;
    }

    const metricKey = config.sortBy === 'sort_on_value_sales' ? 'valueSales' : 'volumeSales';
    sorted.sort((a, b) => a[metricKey] - b[metricKey]);

    // asc = High-Low (descending metric), desc = Low-High (ascending metric)
    if (config.sortOrder === 'asc') {
        sorted.reverse();
    }

    return sorted;
};

const applyOwnEntityFirst = (items: AxisPreviewItem[], config: AxisConfiguration): AxisPreviewItem[] => {
    if (!config.showOwnVendorFirst) {
        return items;
    }

    const ownItems = items.filter((item) => item.isOwn);
    const otherItems = items.filter((item) => !item.isOwn);

    return [...ownItems, ...otherItems];
};

const formatPercent = (value: number) =>
    Number.isFinite(value) ? `${value.toFixed(2)}%` : '0%';

export type ShowTopStats = {
    valueSalesPercent: number;
    volumeSalesPercent: number;
};

export const computeShowTopStats = (
    items: AxisPreviewItem[],
    visibleItems: AxisPreviewItem[],
) => {
    const totalValueSales = items.reduce((sum, item) => sum + item.valueSales, 0);
    const totalVolumeSales = items.reduce((sum, item) => sum + item.volumeSales, 0);
    const visibleValueSales = visibleItems.reduce((sum, item) => sum + item.valueSales, 0);
    const visibleVolumeSales = visibleItems.reduce((sum, item) => sum + item.volumeSales, 0);

    return {
        valueSalesPercent: totalValueSales > 0 ? (visibleValueSales / totalValueSales) * 100 : 0,
        volumeSalesPercent: totalVolumeSales > 0 ? (visibleVolumeSales / totalVolumeSales) * 100 : 0,
    };
};

type BuildAxisPreviewOptions = {
    /** When true, keeps API response order (sort/own-entity already applied server-side). */
    preserveApiOrder?: boolean;
    /** When provided, uses API share KPIs instead of client-side aggregation. */
    showTopStats?: ShowTopStats | null;
};

export const buildAxisPreview = (
    previewItems: AxisPreviewItem[],
    config: AxisConfiguration,
    otherLabel: string,
    options?: BuildAxisPreviewOptions,
): AxisPreviewResult => {
    const orderedItems = options?.preserveApiOrder
        ? previewItems
        : applyOwnEntityFirst(sortPreviewItems(previewItems, config), config);

    if (options?.preserveApiOrder) {
        const regularItems = orderedItems.filter((item) => !item.isOtherGroup);
        const stats =
            options?.showTopStats ?? computeShowTopStats(previewItems, regularItems);

        const entries: PreviewListEntry[] = orderedItems.map((item) => {
            if (item.isOtherGroup) {
                return {
                    kind: 'other',
                    label: otherLabel,
                    hiddenItems: item.members ?? [],
                };
            }

            return {
                kind: 'item',
                name: item.name,
                isOwn: Boolean(item.isOwn),
            };
        });

        return {
            entries,
            visibleCount: entries.length,
            totalCount: regularItems.length,
            valueSalesPercent: stats.valueSalesPercent,
            volumeSalesPercent: stats.volumeSalesPercent,
        };
    }

    const totalCount = orderedItems.length;
    const visibleCount = Math.min(config.showTop, totalCount);
    const visibleItems = orderedItems.slice(0, visibleCount);
    const hiddenItems = orderedItems.slice(visibleCount);
    const stats =
        options?.showTopStats ?? computeShowTopStats(previewItems, visibleItems);

    const entries: PreviewListEntry[] = visibleItems.map((item) => ({
        kind: 'item',
        name: item.name,
        isOwn: Boolean(item.isOwn),
    }));

    if (config.groupRestAsOther && hiddenItems.length > 0) {
        entries.push({
            kind: 'other',
            label: otherLabel,
            hiddenItems: hiddenItems.map((item) => item.name),
        });
    }

    return {
        entries,
        visibleCount: entries.length,
        totalCount,
        valueSalesPercent: stats.valueSalesPercent,
        volumeSalesPercent: stats.volumeSalesPercent,
    };
};

export const formatShowTopStatsLine = (
    showTop: number,
    totalCount: number,
    valueSalesPercent: number,
    volumeSalesPercent: number,
) =>
    `${showTop}/${totalCount} • ${formatPercent(valueSalesPercent)} Value Sales • ${formatPercent(volumeSalesPercent)} Volume Sales`;

export const configsAreEqual = (left: AxisConfiguration, right: AxisConfiguration) =>
    left.sortBy === right.sortBy &&
    left.sortOrder === right.sortOrder &&
    left.showTop === right.showTop &&
    left.groupRestAsOther === right.groupRestAsOther &&
    left.showOwnVendorFirst === right.showOwnVendorFirst;

/** Caps show-top to vendors present in the current chart load (PA-07 preview is main-load only). */
export const clampAxisConfigurationToLoadedCount = (
    config: AxisConfiguration,
    loadedCount: number,
): AxisConfiguration => {
    if (loadedCount <= 0) return config;

    const showTop = Math.min(config.showTop, loadedCount);
    return {
        ...config,
        showTop,
        groupRestAsOther: showTop >= loadedCount ? false : config.groupRestAsOther,
    };
};
